/**
 * ET calendar dates (B-026, CLAUDE.md "Timestamps … EVERYWHERE — NO EXCEPTIONS").
 *
 * "Today", "expires in N days" and "which day did this happen" are ET facts: the desk trades on
 * the New York calendar. Computing them from the viewer's clock (`new Date().setHours(0,0,0,0)`)
 * or from UTC (`toISOString().split('T')[0]`) gives a reader in Tokyo, Honolulu or anyone near
 * midnight ET the wrong day. Every calendar-day computation shown to the user or sent to the API
 * goes through here (or `etDaysToExpiry` in ./expiry). Differences between two instants
 * (countdowns, "3h ago") are timezone-independent and do not need this.
 */
import { etDaysToExpiry } from './expiry';
import { parseInstant } from './format';

const ET = 'America/New_York';

/** The ET calendar date of an instant, as YYYY-MM-DD. */
export function etDateOf(d: Date): string {
  return d.toLocaleDateString('en-CA', { timeZone: ET });
}

/** Today's ET calendar date, YYYY-MM-DD — never the viewer's clock, never UTC. */
export function etToday(now: Date = new Date()): string {
  return etDateOf(now);
}

/** The ET calendar date of a stored timestamp. A naive ISO string is legacy UTC (the backend's
 *  pre-migration shape); a bare YYYY-MM-DD already is a day and is returned as written; an
 *  unparseable one is returned as-is rather than guessed at. */
export function etDateKey(dateStr: string): string {
  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
    const d = parseInstant(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return etDateOf(d);
  } catch {
    return dateStr;
  }
}

/** The "Nd left" badge value: ET calendar days to expiry, or -1 once the expiration has passed
 *  or the date is unusable (the badge's existing contract: -1 renders as "Expired"). */
export function dteBadgeDays(expiration: string | undefined | null, now: Date = new Date()): number {
  const days = etDaysToExpiry(expiration, now);
  return days === null || days < 0 ? -1 : days;
}
